import React, { useState, useEffect } from 'react';
import { 
  Users, Briefcase, Send, TrendingUp, Clock, Search, Filter, 
  UserCircle, Calendar, CheckCircle, XCircle, AlertCircle, 
  Eye, Edit, Plus, RefreshCw, ChevronRight, Award, Target,
  LayoutDashboard, Building2, X, User, Phone, MapPin, FileText, GraduationCap, Info, UserCheck, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import EnhancedCandidateCreateModal from '../components/EnhancedCandidateCreateModal';
import AddSubmissionModal from '../components/AddSubmissionModal';
import VendorManagement from './VendorManagement';
import Submissions from './Submissions';
import LeadCandidateManagement from '../components/LeadCandidateManagement';
import AISourcing from './AISourcing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Pipeline stage configuration with colors and icons
const PIPELINE_STAGES = [
  { key: 'sourced', label: 'Sourced', color: 'bg-gray-100 text-gray-800', icon: Users },
  { key: 'screened', label: 'Screened', color: 'bg-blue-100 text-blue-800', icon: UserCircle },
  { key: 'internally_submitted', label: 'Submitted', color: 'bg-purple-100 text-purple-800', icon: Send },
  { key: 'client_reviewing', label: 'Client Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { key: 'interview_scheduled', label: 'Interview', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
  { key: 'offer_made', label: 'Offer Made', color: 'bg-orange-100 text-orange-800', icon: Award },
  { key: 'offer_accepted', label: 'Accepted', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
];

const RecruiterRileyDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [showAddSubmissionModal, setShowAddSubmissionModal] = useState(false);
  const [selectedJobForSubmission, setSelectedJobForSubmission] = useState(null);
  const [stats, setStats] = useState({
    myCandidates: 0,
    activeSubmissions: 0,
    interviews: 0,
    offers: 0,
    openJobs: 0
  });
  const [candidates, setCandidates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCandidateForStatus, setSelectedCandidateForStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedCandidateForEdit, setSelectedCandidateForEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Mock current recruiter (in production, get from auth context)
  const currentRecruiter = {
    id: '490fcd28-049c-4564-8771-52835b432bcc', // junoadmin ID for testing
    name: 'Recruiter Riley',
    email: 'riley@junotalent.com'
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Clear search term when switching tabs
  useEffect(() => {
    setSearchTerm('');
  }, [activeTab]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch candidates, submissions, and jobs
      await Promise.all([
        fetchMyCandidates(),
        fetchMySubmissions(),
        fetchAllJobs()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCandidates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not wrapped in {candidates: [...]}
        const allCandidates = Array.isArray(data) ? data : (data.candidates || []);
        
        // Filter candidates based on Riley's access rules:
        // 1. Created by Riley
        // 2. Assigned to Riley (primary recruiter)
        // 3. Lock expired (can be reassigned)
        const today = new Date();
        const myCandidates = allCandidates.filter(c => {
          // Created by me
          if (c.created_by === currentRecruiter.id || c.recruited_by === currentRecruiter.id) {
            return true;
          }
          // Assigned to me
          if (c.primary_recruiter_id === currentRecruiter.id) {
            return true;
          }
          // Lock expired (30 days passed)
          if (c.primary_recruiter_lock_until) {
            const lockDate = new Date(c.primary_recruiter_lock_until);
            if (today > lockDate) {
              return true; // Lock expired, available
            }
          }
          return false;
        });
        
        // If no assigned candidates, show all candidates for demo purposes
        const candidatesToShow = myCandidates.length > 0 ? myCandidates : allCandidates;
        
        setCandidates(candidatesToShow);
        setStats(prev => ({ ...prev, myCandidates: candidatesToShow.length }));
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        const allSubmissions = data.submissions || [];
        
        // For now, show all submissions (in production, filter by recruiter_id)
        // TODO: Filter by actual logged-in recruiter once auth is implemented
        const mySubmissions = allSubmissions;
        
        setSubmissions(mySubmissions);
        
        // Calculate pipeline data
        const pipeline = {};
        PIPELINE_STAGES.forEach(stage => {
          pipeline[stage.key] = mySubmissions.filter(s => 
            s.current_status === stage.key
          ).length;
        });
        setPipelineData(pipeline);
        
        // Calculate stats
        const activeSubmissions = mySubmissions.filter(s => 
          !['offer_accepted', 'rejected', 'internally_rejected', 'client_rejected'].includes(s.current_status)
        ).length;
        
        const interviews = mySubmissions.filter(s => 
          ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
        ).length;
        
        const offers = mySubmissions.filter(s => 
          ['offer_made', 'offer_accepted'].includes(s.current_status)
        ).length;
        
        setStats(prev => ({ 
          ...prev, 
          activeSubmissions,
          interviews,
          offers
        }));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchAllJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not wrapped in {jobs: [...]}
        const allJobs = Array.isArray(data) ? data : (data.jobs || []);
        // Filter only open jobs
        const openJobs = allJobs.filter(j => j.status === 'open');
        setJobs(openJobs);
        setStats(prev => ({ ...prev, openJobs: openJobs.length }));
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const canReassignCandidate = (candidate) => {
    if (!candidate.primary_recruiter_lock_until) return true;
    const lockDate = new Date(candidate.primary_recruiter_lock_until);
    return new Date() > lockDate;
  };

  const handleUpdateCandidateStatus = async (newStatus) => {
    if (!selectedCandidateForStatus) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates/${selectedCandidateForStatus.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_status: newStatus
        })
      });

      if (response.ok) {
        toast.success(`Candidate status updated to ${newStatus}`);
        setShowStatusModal(false);
        setSelectedCandidateForStatus(null);
        fetchMyCandidates(); // Refresh data
      } else {
        toast.error('Failed to update candidate status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating candidate status');
    }
  };

  // Candidate status options for Riley
  const CANDIDATE_STATUSES = [
    { value: 'sourced', label: 'Sourced', icon: '🔍', color: 'purple' },
    { value: 'screened', label: 'Screened', icon: '✅', color: 'blue' },
    { value: 'available', label: 'Available', icon: '🟢', color: 'green' },
    { value: 'rejected', label: 'Rejected', icon: '❌', color: 'red' },
    { value: 'unavailable', label: 'Unavailable', icon: '🔒', color: 'gray' }
  ];

  // Overview Tab Component
  const OverviewTab = () => {
    const [selectedMetric, setSelectedMetric] = useState(null);

    // Filter data based on selected metric
    const getFilteredData = () => {
      switch(selectedMetric) {
        case 'submissions':
          return submissions.filter(s => s.status !== 'rejected' && s.status !== 'offer_accepted');
        case 'jobs':
          return jobs;
        case 'interviews':
          return submissions.filter(s => s.status === 'interview_scheduled');
        case 'offers':
          return submissions.filter(s => s.status === 'offer_made' || s.status === 'offer_extended');
        case 'onboards':
          return submissions.filter(s => s.status === 'offer_accepted' || s.status === 'onboarding');
        default:
          return [];
      }
    };

    const filteredData = getFilteredData();

    return (
      <div className="space-y-6">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div 
            onClick={() => setSelectedMetric('submissions')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Current Open Submissions</p>
                <p className="text-3xl font-bold mt-1">{stats.activeSubmissions}</p>
              </div>
              <Send className="h-12 w-12 text-purple-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('jobs')}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Current Open Jobs</p>
                <p className="text-3xl font-bold mt-1">{stats.openJobs}</p>
              </div>
              <Briefcase className="h-12 w-12 text-orange-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('interviews')}
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">Current Interviews</p>
                <p className="text-3xl font-bold mt-1">{stats.interviews}</p>
              </div>
              <Calendar className="h-12 w-12 text-indigo-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('offers')}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Current Offers in Progress</p>
                <p className="text-3xl font-bold mt-1">{stats.offers}</p>
              </div>
              <Award className="h-12 w-12 text-green-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('onboards')}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Current Onboards in Progress</p>
                <p className="text-3xl font-bold mt-1">
                  {submissions.filter(s => s.status === 'offer_accepted' || s.status === 'onboarding').length}
                </p>
              </div>
              <UserCheck className="h-12 w-12 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Data Table - Shows when metric is clicked */}
        {selectedMetric && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMetric === 'submissions' && 'Open Submissions'}
                {selectedMetric === 'jobs' && 'Open Jobs'}
                {selectedMetric === 'interviews' && 'Candidates in Interviews'}
                {selectedMetric === 'offers' && 'Offers in Progress'}
                {selectedMetric === 'onboards' && 'Onboarding in Progress'}
              </h3>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              {selectedMetric === 'jobs' ? (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(job => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{job.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.client || job.client_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.location || 'Remote'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {job.status || 'Open'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submissions.filter(s => s.job_id === job.id).length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(submission => (
                      <tr key={submission.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {submission.candidate_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.job_title || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {(submission.current_status || submission.status)?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.submitted_at ? formatDate(submission.submitted_at) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.submitted_by_name || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {filteredData.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Recent Candidates */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Candidates</h3>
          <div className="space-y-3">
            {candidates.slice(0, 5).map(candidate => (
              <div key={candidate.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {candidate.name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{candidate.name}</p>
                    <p className="text-sm text-gray-500">{candidate.current_title || 'N/A'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{formatDate(candidate.created_at)}</p>
                  {!canReassignCandidate(candidate) && (
                    <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      Locked
                    </span>
                  )}
                </div>
              </div>
            ))}
            {candidates.length === 0 && (
              <p className="text-center text-gray-500 py-4">No candidates yet</p>
            )}
          </div>
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Submissions</h3>
          <div className="space-y-3">
            {submissions.slice(0, 5).map(submission => {
              const stage = PIPELINE_STAGES.find(s => s.key === submission.current_status);
              return (
                <div key={submission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <p className="font-medium text-gray-900">{submission.candidate_name}</p>
                    <p className="text-sm text-gray-500">{submission.job_title}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${stage?.color || 'bg-gray-100 text-gray-800'}`}>
                    {stage?.label || submission.current_status}
                  </span>
                </div>
              );
            })}
            {submissions.length === 0 && (
              <p className="text-center text-gray-500 py-4">No submissions yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  // My Candidates Tab - Memoized to prevent input focus loss
  const MyCandidatesTab = React.useMemo(() => {
    const filteredCandidates = candidates.filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.current_title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-4">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowAddCandidateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Candidate
            </button>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Primary Recruiter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCandidates.map(candidate => (
                <tr key={candidate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {candidate.name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{candidate.name}</p>
                        <p className="text-sm text-gray-500">{candidate.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {candidate.current_title || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {candidate.location || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{candidate.primary_recruiter_name || 'Unassigned'}</span>
                      {!canReassignCandidate(candidate) && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          <Clock className="h-3 w-3" />
                          Locked
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(candidate.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      candidate.candidate_status === 'available' ? 'bg-green-100 text-green-800' :
                      candidate.candidate_status === 'screened' ? 'bg-blue-100 text-blue-800' :
                      candidate.candidate_status === 'sourced' ? 'bg-purple-100 text-purple-800' :
                      candidate.candidate_status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {candidate.candidate_status || 'sourced'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedCandidateForEdit(candidate);
                          setShowEditModal(true);
                        }}
                        className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCandidateForStatus(candidate);
                          setShowStatusModal(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Status
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCandidates.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No candidates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [candidates, searchTerm, showAddCandidateModal]);

  // My Submissions Tab - DEPRECATED (now using Submissions component)
  // This component is no longer used as we've consolidated to the Submissions page
  /*
  const MySubmissionsTab = () => {
    // Group submissions by job
    const submissionsByJob = {};
    submissions.forEach(sub => {
      if (!submissionsByJob[sub.job_id]) {
        submissionsByJob[sub.job_id] = {
          job_id: sub.job_id,
          job_title: sub.job_title,
          client_name: sub.client_name,
          submissions: []
        };
      }
      submissionsByJob[sub.job_id].submissions.push(sub);
    });

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600">
            Showing jobs where you have submitted candidates ({Object.keys(submissionsByJob).length} jobs)
          </p>
        </div>

        {Object.values(submissionsByJob).map(jobGroup => (
          <div key={jobGroup.job_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{jobGroup.job_title}</h3>
                  <p className="text-sm text-gray-500">{jobGroup.client_name}</p>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {jobGroup.submissions.length} candidate{jobGroup.submissions.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {jobGroup.submissions.map(submission => {
                const stage = PIPELINE_STAGES.find(s => s.key === submission.current_status);
                const StageIcon = stage?.icon || AlertCircle;
                return (
                  <div key={submission.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {submission.candidate_name?.charAt(0) || 'C'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{submission.candidate_name}</p>
                          <p className="text-sm text-gray-500">
                            Submitted {formatDate(submission.submission_date)} by {submission.submitted_by_name || submission.recruiter_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${stage?.color || 'bg-gray-100 text-gray-800'}`}>
                          <StageIcon className="h-4 w-4" />
                          {stage?.label || submission.current_status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {Object.keys(submissionsByJob).length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Send className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No submissions yet</p>
          </div>
        )}
      </div>
    );
  };
  */

  // Open Jobs Tab - Tabular View with Details Modal
  const OpenJobsTab = () => {
    const [selectedJob, setSelectedJob] = useState(null);
    const [showJobDetailModal, setShowJobDetailModal] = useState(false);
    
    const filteredJobs = jobs.filter(j =>
      j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleJobClick = (job) => {
      setSelectedJob(job);
      setShowJobDetailModal(true);
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs by title, company, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Tabular View */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map(job => (
                  <tr 
                    key={job.id}
                    onClick={() => handleJobClick(job)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="h-5 w-5 text-purple-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{job.title}</div>
                          {job.department && (
                            <div className="text-xs text-gray-500">{job.department}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.company || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{job.location || 'Remote'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {job.employment_type || 'Full-time'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJobClick(job);
                        }}
                        className="text-purple-600 hover:text-purple-900 flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredJobs.length === 0 && (
            <div className="p-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No open jobs found</p>
            </div>
          )}
        </div>

        {/* Job Detail Modal */}
        {showJobDetailModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-1">{selectedJob.title}</h2>
                  <p className="text-purple-100">{selectedJob.company}</p>
                </div>
                <button
                  onClick={() => {
                    setShowJobDetailModal(false);
                    setSelectedJob(null);
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Key Decision-Making Information - Highlighted Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Key Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Client</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.client || selectedJob.client_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Job Opened</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.created_at ? formatDate(selectedJob.created_at) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Current Submissions</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {submissions.filter(s => s.job_id === selectedJob.id).length} candidates
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Sales Manager</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {selectedJob.account_manager || selectedJob.hiring_manager || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.location || 'Remote'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.employment_type || 'Full-time'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{selectedJob.no_of_openings || 1} {selectedJob.no_of_openings === 1 ? 'opening' : 'openings'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-600 font-medium">{selectedJob.status || 'Active'}</span>
                    </div>
                  </div>

                  {/* Job Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Job Description</h3>
                    <div className="prose prose-sm max-w-none text-gray-600">
                      {selectedJob.description || 'No description available.'}
                    </div>
                  </div>

                  {/* Requirements */}
                  {selectedJob.requirements && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        {(Array.isArray(selectedJob.requirements) 
                          ? selectedJob.requirements 
                          : selectedJob.requirements.split('\n')
                        ).map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Skills */}
                  {selectedJob.required_skills && selectedJob.required_skills.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Required Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedJob.required_skills.map((skill, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compensation */}
                  {(selectedJob.salary_range_min || selectedJob.salary_range_max) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Compensation</h3>
                      <p className="text-gray-600">
                        ${selectedJob.salary_range_min?.toLocaleString()} - ${selectedJob.salary_range_max?.toLocaleString()}
                        {selectedJob.salary_currency && ` ${selectedJob.salary_currency}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
                <button
                  onClick={() => {
                    setShowJobDetailModal(false);
                    setSelectedJob(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setSelectedJobForSubmission(selectedJob);
                    setShowJobDetailModal(false);
                    setShowAddSubmissionModal(true);
                  }}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Submit Candidate to this Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Enhanced Candidate Edit Modal Component (inline)
  const EnhancedCandidateEditModal = () => {
    const [activeTab, setActiveTab] = useState('basic');
    const [formData, setFormData] = useState({
      // Pre-populate all fields from selectedCandidateForEdit
      first_name: selectedCandidateForEdit.first_name || selectedCandidateForEdit.name?.split(' ')[0] || '',
      last_name: selectedCandidateForEdit.last_name || selectedCandidateForEdit.name?.split(' ').slice(1).join(' ') || '',
      preferred_name: selectedCandidateForEdit.preferred_name || '',
      email: selectedCandidateForEdit.email || '',
      phone: selectedCandidateForEdit.phone || '',
      mobile_phone: selectedCandidateForEdit.mobile_phone || '',
      alternate_email: selectedCandidateForEdit.alternate_email || '',
      location: selectedCandidateForEdit.location || '',
      address_line1: selectedCandidateForEdit.address_line1 || '',
      address_line2: selectedCandidateForEdit.address_line2 || '',
      city: selectedCandidateForEdit.city || '',
      state: selectedCandidateForEdit.state || '',
      postal_code: selectedCandidateForEdit.postal_code || '',
      country: selectedCandidateForEdit.country || 'United States',
      work_authorization_status: selectedCandidateForEdit.work_authorization_status || 'authorized',
      visa_status: selectedCandidateForEdit.visa_status || '',
      eligible_to_work_us: selectedCandidateForEdit.eligible_to_work_us !== undefined ? selectedCandidateForEdit.eligible_to_work_us : true,
      requires_sponsorship: selectedCandidateForEdit.requires_sponsorship || false,
      current_title: selectedCandidateForEdit.current_title || '',
      current_company: selectedCandidateForEdit.current_company || '',
      current_salary: selectedCandidateForEdit.current_salary || '',
      salary_expectations: selectedCandidateForEdit.salary_expectations || { min: '', max: '', currency: 'USD' },
      notice_period_days: selectedCandidateForEdit.notice_period_days || '',
      availability_date: selectedCandidateForEdit.availability_date || '',
      total_experience_years: selectedCandidateForEdit.total_experience_years || selectedCandidateForEdit.years_of_experience || 0,
      relevant_experience_years: selectedCandidateForEdit.relevant_experience_years || '',
      skills: selectedCandidateForEdit.skills || [],
      technical_skills: selectedCandidateForEdit.technical_skills || [],
      certifications: selectedCandidateForEdit.certifications || [],
      desired_job_titles: selectedCandidateForEdit.desired_job_titles || [],
      preferred_locations: selectedCandidateForEdit.preferred_locations || [],
      job_search_status: selectedCandidateForEdit.job_search_status || 'active',
      willing_to_relocate: selectedCandidateForEdit.willing_to_relocate || false,
      remote_work_preference: selectedCandidateForEdit.remote_work_preference || 'flexible',
      summary: selectedCandidateForEdit.summary || '',
      professional_summary: selectedCandidateForEdit.professional_summary || '',
      career_objectives: selectedCandidateForEdit.career_objectives || '',
      linkedin_url: selectedCandidateForEdit.linkedin_url || '',
      portfolio_url: selectedCandidateForEdit.portfolio_url || '',
      github_url: selectedCandidateForEdit.github_url || '',
      source: selectedCandidateForEdit.source || 'manual',
      referrer_name: selectedCandidateForEdit.referrer_name || '',
      engagement_type: selectedCandidateForEdit.engagement_type || 'w2',
      vendor_id: selectedCandidateForEdit.vendor_id || '',
      candidate_priority: selectedCandidateForEdit.candidate_priority || 'medium',
      tags: selectedCandidateForEdit.tags || [],
      resume_file_url: selectedCandidateForEdit.resume_file_url || '',
      cover_letter_file_url: selectedCandidateForEdit.cover_letter_file_url || ''
    });
    
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);

    useEffect(() => {
      fetchVendors();
    }, []);

    const fetchVendors = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/companies`);
        if (response.ok) {
          const data = await response.json();
          setVendors(data.companies || []);
        }
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    const handleArrayFieldChange = (field, value) => {
      const items = value.split(',').map(item => item.trim()).filter(item => item);
      setFormData({ ...formData, [field]: items });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        const submitData = {
          ...formData,
          // Ensure name is set from first_name + last_name
          name: `${formData.first_name} ${formData.last_name}`.trim(),
          total_experience_years: parseInt(formData.total_experience_years) || 0,
          relevant_experience_years: formData.relevant_experience_years ? parseInt(formData.relevant_experience_years) : null,
          current_salary: formData.current_salary ? parseFloat(formData.current_salary) : null,
          notice_period_days: formData.notice_period_days ? parseInt(formData.notice_period_days) : null
        };

        const response = await fetch(`${BACKEND_URL}/api/candidates/${selectedCandidateForEdit.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        });

        if (response.ok) {
          toast.success('Candidate updated successfully!');
          setShowEditModal(false);
          setSelectedCandidateForEdit(null);
          fetchMyCandidates();
        } else {
          const errorData = await response.json();
          toast.error(`Failed to update candidate: ${errorData.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating candidate:', error);
        toast.error('Error updating candidate');
      } finally {
        setLoading(false);
      }
    };

    const tabs = [
      { id: 'basic', label: 'Basic Info', icon: User },
      { id: 'contact', label: 'Contact & Address', icon: Phone },
      { id: 'professional', label: 'Professional', icon: Briefcase },
      { id: 'experience', label: 'Experience & Skills', icon: GraduationCap },
      { id: 'preferences', label: 'Preferences', icon: MapPin },
      { id: 'additional', label: 'Additional Info', icon: FileText }
    ];

    const renderTabContent = () => {
      switch (activeTab) {
        case 'basic':
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name</label>
                <input
                  type="text"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Johnny (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="john.smith@example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                  <input
                    type="tel"
                    value={formData.mobile_phone}
                    onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="+1 (555) 987-6543"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Authorization Status</label>
                <select
                  value={formData.work_authorization_status}
                  onChange={(e) => setFormData({ ...formData, work_authorization_status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="authorized">Authorized to work</option>
                  <option value="needs_sponsorship">Needs sponsorship</option>
                  <option value="student_visa">Student visa</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.eligible_to_work_us}
                    onChange={(e) => setFormData({ ...formData, eligible_to_work_us: e.target.checked })}
                    className="rounded border-gray-300 text-purple-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Eligible to work in US</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requires_sponsorship}
                    onChange={(e) => setFormData({ ...formData, requires_sponsorship: e.target.checked })}
                    className="rounded border-gray-300 text-purple-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Requires sponsorship</span>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Engagement Type</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Engagement Type</label>
                  <select
                    value={formData.engagement_type}
                    onChange={(e) => setFormData({ ...formData, engagement_type: e.target.value, vendor_id: e.target.value !== 'contingent_vendor' ? '' : formData.vendor_id })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="w2">W2 - Direct Employee</option>
                    <option value="contingent_1099">Contingent 1099 - Independent Contractor</option>
                    <option value="contingent_vendor">Contingent Vendor - Through Staffing Agency</option>
                  </select>
                </div>

                {formData.engagement_type === 'contingent_vendor' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor/Staffing Agency</label>
                    <select
                      value={formData.vendor_id}
                      onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select a vendor...</option>
                      {vendors.map(vendor => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.company_name || vendor.vendor_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          );

        case 'contact':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Dallas, TX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="123 Main Street"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={formData.address_line2}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Apartment, suite, etc."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Dallas"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="TX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="75201"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="India">India</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Email</label>
                <input
                  type="email"
                  value={formData.alternate_email}
                  onChange={(e) => setFormData({ ...formData, alternate_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="alternate@example.com"
                />
              </div>
            </div>
          );

        case 'professional':
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Job Title</label>
                  <input
                    type="text"
                    value={formData.current_title}
                    onChange={(e) => setFormData({ ...formData, current_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Senior Software Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Company</label>
                  <input
                    type="text"
                    value={formData.current_company}
                    onChange={(e) => setFormData({ ...formData, current_company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tech Corp Inc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Salary ($)</label>
                  <input
                    type="number"
                    value={formData.current_salary}
                    onChange={(e) => setFormData({ ...formData, current_salary: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="120000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period (Days)</label>
                  <input
                    type="number"
                    value={formData.notice_period_days}
                    onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="14"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Expectations</label>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="number"
                    value={formData.salary_expectations.min}
                    onChange={(e) => setFormData({
                      ...formData,
                      salary_expectations: { ...formData.salary_expectations, min: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    value={formData.salary_expectations.max}
                    onChange={(e) => setFormData({
                      ...formData,
                      salary_expectations: { ...formData.salary_expectations, max: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Max"
                  />
                  <select
                    value={formData.salary_expectations.currency}
                    onChange={(e) => setFormData({
                      ...formData,
                      salary_expectations: { ...formData.salary_expectations, currency: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability Date</label>
                <input
                  type="date"
                  value={formData.availability_date}
                  onChange={(e) => setFormData({ ...formData, availability_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Search Status</label>
                <select
                  value={formData.job_search_status}
                  onChange={(e) => setFormData({ ...formData, job_search_status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="active">Actively looking</option>
                  <option value="passive">Passively looking</option>
                  <option value="not_looking">Not looking</option>
                  <option value="employed">Currently employed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
                <textarea
                  value={formData.professional_summary}
                  onChange={(e) => setFormData({ ...formData, professional_summary: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Brief professional summary..."
                />
              </div>
            </div>
          );

        case 'experience':
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Experience (Years)</label>
                  <input
                    type="number"
                    value={formData.total_experience_years}
                    onChange={(e) => setFormData({ ...formData, total_experience_years: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relevant Experience (Years)</label>
                  <input
                    type="number"
                    value={formData.relevant_experience_years}
                    onChange={(e) => setFormData({ ...formData, relevant_experience_years: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.skills.join(', ')}
                  onChange={(e) => handleArrayFieldChange('skills', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="JavaScript, React, Node.js, Python"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Technical Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.technical_skills.join(', ')}
                  onChange={(e) => handleArrayFieldChange('technical_skills', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="AWS, Docker, Kubernetes, MySQL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certifications <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.certifications.join(', ')}
                  onChange={(e) => handleArrayFieldChange('certifications', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="AWS Certified, PMP, Scrum Master"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desired Job Titles <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.desired_job_titles.join(', ')}
                  onChange={(e) => handleArrayFieldChange('desired_job_titles', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Software Engineer, Full Stack Developer, Technical Lead"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Career Objectives</label>
                <textarea
                  value={formData.career_objectives}
                  onChange={(e) => setFormData({ ...formData, career_objectives: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Career goals and aspirations..."
                />
              </div>
            </div>
          );

        case 'preferences':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Locations <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.preferred_locations.join(', ')}
                  onChange={(e) => handleArrayFieldChange('preferred_locations', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Dallas TX, Austin TX, Remote"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remote Work Preference</label>
                <select
                  value={formData.remote_work_preference}
                  onChange={(e) => setFormData({ ...formData, remote_work_preference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="remote_only">Remote Only</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">Onsite Only</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.willing_to_relocate}
                    onChange={(e) => setFormData({ ...formData, willing_to_relocate: e.target.checked })}
                    className="rounded border-gray-300 text-purple-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Willing to relocate</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Priority</label>
                <select
                  value={formData.candidate_priority}
                  onChange={(e) => setFormData({ ...formData, candidate_priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="hot">Hot</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags <span className="text-xs text-gray-500">(Comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={(e) => handleArrayFieldChange('tags', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="senior, react-expert, startup-experience"
                />
              </div>
            </div>
          );

        case 'additional':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio URL</label>
                <input
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://portfolio.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GitHub URL</label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://github.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="indeed">Indeed</option>
                  <option value="monster">Monster</option>
                  <option value="referral">Referral</option>
                  <option value="career_site">Career Site</option>
                  <option value="agency">Agency</option>
                </select>
              </div>

              {formData.source === 'referral' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referrer Name</label>
                  <input
                    type="text"
                    value={formData.referrer_name}
                    onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Name of person who referred this candidate"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resume File URL</label>
                <input
                  type="url"
                  value={formData.resume_file_url}
                  onChange={(e) => setFormData({ ...formData, resume_file_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://example.com/resume.pdf"
                />
                <p className="mt-1 text-xs text-gray-500">Note: File upload functionality excluded from edit mode</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter File URL</label>
                <input
                  type="url"
                  value={formData.cover_letter_file_url}
                  onChange={(e) => setFormData({ ...formData, cover_letter_file_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://example.com/cover-letter.pdf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">General Summary</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="General candidate summary and notes..."
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Edit Candidate Information</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCandidateForEdit(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-6 mt-4 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit} className="h-full flex flex-col">
              <div className="flex-1 p-6">
                {renderTabContent()}
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    * Required fields
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedCandidateForEdit(null);
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-green-600 rounded-full flex items-center justify-center text-white">
              <Users className="h-6 w-6" />
            </div>
            Recruiter Riley Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Manage your candidates, submissions, and pipeline</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { key: 'overview', label: 'Overview', icon: LayoutDashboard },
              { key: 'candidates', label: 'My Candidates', icon: Users },
              { key: 'ai-sourcing', label: 'AI Sourcing', icon: Sparkles },
              { key: 'jobs', label: 'Open Jobs', icon: Briefcase },
              { key: 'submission-mgmt', label: 'Client Submissions', icon: Send },
              { key: 'vendor-mgmt', label: 'Vendor Management', icon: Building2 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab />}
              {activeTab === 'ai-sourcing' && <AISourcing />}
              {activeTab === 'candidates' && MyCandidatesTab}
              {activeTab === 'jobs' && <OpenJobsTab />}
              {activeTab === 'vendor-mgmt' && <VendorManagement />}
              {activeTab === 'submission-mgmt' && <Submissions />}
            </>
          )}
        </div>
      </div>

      {/* Add Candidate Modal */}
      {showAddCandidateModal && (
        <EnhancedCandidateCreateModal
          isOpen={showAddCandidateModal}
          onClose={() => setShowAddCandidateModal(false)}
          onSuccess={() => {
            setShowAddCandidateModal(false);
            fetchDashboardData();
            toast.success('Candidate added successfully!');
          }}
        />
      )}

      {/* Add Submission Modal */}
      {showAddSubmissionModal && (
        <AddSubmissionModal
          isOpen={showAddSubmissionModal}
          onClose={() => {
            setShowAddSubmissionModal(false);
            setSelectedJobForSubmission(null);
          }}
          selectedJob={selectedJobForSubmission}
          onSubmissionCreated={() => {
            setShowAddSubmissionModal(false);
            setSelectedJobForSubmission(null);
            fetchDashboardData();
            toast.success('Candidate submitted successfully!');
          }}
        />
      )}

      {/* Update Candidate Status Modal */}
      {showStatusModal && selectedCandidateForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Update Candidate Status</h2>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedCandidateForStatus(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Candidate:</p>
                <p className="font-semibold text-gray-900">{selectedCandidateForStatus.name}</p>
                <p className="text-sm text-gray-500">{selectedCandidateForStatus.email}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Current Status:</p>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                  {selectedCandidateForStatus.candidate_status || 'sourced'}
                </span>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Select New Status:</label>
                <div className="space-y-2">
                  {CANDIDATE_STATUSES.map((status) => (
                    <button
                      key={status.value}
                      onClick={() => handleUpdateCandidateStatus(status.value)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                        selectedCandidateForStatus.candidate_status === status.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-2xl">{status.icon}</span>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900">{status.label}</p>
                      </div>
                      {selectedCandidateForStatus.candidate_status === status.value && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedCandidateForStatus(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Edit Candidate Modal */}
      {showEditModal && selectedCandidateForEdit && <EnhancedCandidateEditModal />}
    </div>
  );
};

export default RecruiterRileyDashboard;
